import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { FaqRow } from "./HelpCenter";
import { Check, X, ArrowRight, Gift, Sparkles } from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

const FLEET_TIERS = [
  { key: "s", label: "1 – 10 véhicules" },
  { key: "m", label: "11 – 30 véhicules" },
  { key: "l", label: "31 – 60 véhicules" },
  { key: "xl", label: "60+ véhicules" },
];

const PLANS = [
  {
    id: "essentiel",
    name: "Essentiel",
    tagline: "Pilotez votre flotte au quotidien.",
    prices: { s: 29, m: 59, l: 99, xl: null },
    features: [
      "Pilotage & calendrier interactif",
      "Réservations & devis illimités",
      "Fiches véhicules complètes",
      "Suivi d'entretien & alertes documents",
      "CRM clients & prospects",
      "Statistiques de revenus",
    ],
    dark: false,
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Automatisez tout, du contrat à l'encaissement.",
    prices: { s: 49, m: 89, l: 149, xl: null },
    features: [
      "Tout le plan Essentiel",
      "Contrats générés automatiquement",
      "Signature électronique",
      "États des lieux photo départ / retour",
      "Encaissements en ligne via Stripe",
      "Champs personnalisés & statuts avancés",
      "Support prioritaire",
    ],
    dark: true,
    popular: true,
  },
];

const COMPARE_ROWS = [
  { label: "Calendrier & planning interactif", essentiel: true, pro: true },
  { label: "Réservations & devis illimités", essentiel: true, pro: true },
  { label: "Fiches véhicules & documents", essentiel: true, pro: true },
  { label: "Suivi d'entretien & alertes", essentiel: true, pro: true },
  { label: "CRM clients & prospects", essentiel: true, pro: true },
  { label: "Statistiques de revenus", essentiel: true, pro: true },
  { label: "Contrats générés automatiquement", essentiel: false, pro: true },
  { label: "Signature électronique", essentiel: false, pro: true },
  { label: "États des lieux numériques", essentiel: false, pro: true },
  { label: "Encaissements en ligne (Stripe)", essentiel: false, pro: true },
  { label: "Champs personnalisés & statuts avancés", essentiel: false, pro: true },
  { label: "Support prioritaire", essentiel: false, pro: true },
];

const PRICING_FAQ = [
  {
    q: "Suis-je engagé si je fais l'essai gratuit ?",
    a: "Non. Pendant les 30 jours d'essai, vous n'êtes pas engagé et aucune carte bancaire n'est demandée. Vous pouvez arrêter à tout moment, sans frais.",
  },
  {
    q: "Que se passe-t-il à la fin de l'essai gratuit ?",
    a: "Votre compte passe en lecture seule : vos données restent accessibles mais vous ne pouvez plus créer de réservations ni de contrats. Choisissez un plan pour réactiver votre compte instantanément.",
  },
  {
    q: "Puis-je changer de plan ou de palier de flotte ?",
    a: "Oui, à tout moment. Si votre flotte grandit, le passage au palier supérieur se fait en un clic depuis vos paramétrages, au prorata de la période en cours.",
  },
  {
    q: "Est-ce compliqué de mettre en place Junago ?",
    a: "Pas du tout. Vous êtes opérationnel en quelques minutes : créez votre compte, ajoutez vos véhicules et vos informations d'entreprise. Notre équipe support vous accompagne si besoin.",
  },
];

export default function Pricing() {
  const [tier, setTier] = useState("s");
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    document.title = "Tarifs — Logiciel de gestion de flotte | Junago";
  }, []);

  const priceFor = (plan) => {
    const base = plan.prices[tier];
    if (base == null) return null;
    return annual ? Math.round(base * 0.9) : base;
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-14 md:pt-20 pb-10 text-center" data-testid="pricing-hero">
        <div className={`${OVERLINE} mb-4`}>Tarifs</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-slate-900" data-testid="pricing-title">
          Un plan adapté
          <br />
          à votre <span className="text-violet-600">flotte</span>.
        </h1>
        <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
          Des formules qui suivent la taille de votre parc. Sans engagement, sans surprise.
        </p>
        <div className="mt-5 flex items-center justify-center gap-4 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5"><Gift className="h-4 w-4 text-violet-600" /> 30 jours d'essai gratuit</span>
          <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-violet-600" /> Sans engagement</span>
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-5xl mx-auto px-6" data-testid="pricing-controls">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <div className="flex flex-wrap justify-center gap-1.5 p-1.5 rounded-2xl bg-slate-100">
            {FLEET_TIERS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTier(t.key)}
                data-testid={`fleet-tier-${t.key}`}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  tier === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-slate-100">
            <button
              onClick={() => setAnnual(false)}
              data-testid="billing-monthly"
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${!annual ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setAnnual(true)}
              data-testid="billing-annual"
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${annual ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Annuel <span className="text-emerald-600">−10%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-4xl mx-auto px-6 py-12" data-testid="pricing-plans">
        <div className="grid md:grid-cols-2 gap-6">
          {PLANS.map((plan) => {
            const price = priceFor(plan);
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl p-8 border transition-shadow ${
                  plan.dark
                    ? "bg-slate-950 text-white border-slate-950 shadow-[0_24px_60px_-16px_rgba(15,23,42,0.5)]"
                    : "bg-white border-slate-200"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-violet-600 text-white text-[10px] font-bold tracking-[0.12em] uppercase">
                    <Sparkles className="h-3 w-3" /> Le plus choisi
                  </span>
                )}
                <div className={`font-heading text-xl font-semibold ${plan.dark ? "text-white" : "text-slate-900"}`}>{plan.name}</div>
                <p className={`mt-1 text-sm ${plan.dark ? "text-slate-400" : "text-slate-500"}`}>{plan.tagline}</p>

                <div className="mt-6 flex items-end gap-2" data-testid={`plan-price-${plan.id}`}>
                  {price != null ? (
                    <>
                      <span className={`font-heading text-5xl font-medium tracking-tight ${plan.dark ? "text-white" : "text-slate-900"}`}>
                        {price}€
                      </span>
                      <span className={`pb-1.5 text-xs ${plan.dark ? "text-slate-400" : "text-slate-500"}`}>
                        HT / mois{annual ? " · facturé annuellement" : ""}
                      </span>
                    </>
                  ) : (
                    <span className={`font-heading text-3xl font-medium tracking-tight ${plan.dark ? "text-white" : "text-slate-900"}`}>
                      Sur devis
                    </span>
                  )}
                </div>

                <ul className="mt-7 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${plan.dark ? "bg-violet-500/25" : "bg-violet-100"}`}>
                        <Check className={`h-3 w-3 ${plan.dark ? "text-violet-300" : "text-violet-600"}`} strokeWidth={3} />
                      </span>
                      <span className={plan.dark ? "text-slate-300" : "text-slate-700"}>{f}</span>
                    </li>
                  ))}
                </ul>

                {price != null ? (
                  <Link
                    to="/register?context=vendor"
                    data-testid={`plan-cta-${plan.id}`}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold ${
                      plan.dark ? "btn-jn-light" : "btn-primary"
                    }`}
                  >
                    Commencer l'essai gratuit <ArrowRight className="btn-arrow h-4 w-4" />
                  </Link>
                ) : (
                  <a
                    href="mailto:support@junago.fr"
                    data-testid={`plan-cta-${plan.id}`}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold ${
                      plan.dark ? "btn-jn-ghost-dark" : "btn-jn-secondary"
                    }`}
                  >
                    Contacter l'équipe
                  </a>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">
          Prix HT. Essai gratuit de 30 jours sans carte bancaire · résiliable en 1 clic.
        </p>
      </section>

      {/* Comparison table */}
      <section className="max-w-4xl mx-auto px-6 py-12" data-testid="pricing-compare">
        <div className="text-center mb-8">
          <div className={`${OVERLINE} mb-2`}>Comparatif</div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
            Comparez les plans
          </h2>
        </div>
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">Fonctionnalité</th>
                  <th className="px-6 py-4 font-semibold text-slate-900 w-28">Essentiel</th>
                  <th className="px-6 py-4 font-semibold text-violet-700 w-28">Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td className="px-6 py-3.5 text-slate-700">{row.label}</td>
                    <td className="px-6 py-3.5 text-center">
                      {row.essentiel ? <Check className="h-4 w-4 text-violet-600 mx-auto" strokeWidth={3} /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                    </td>
                    <td className="px-6 py-3.5 text-center bg-violet-50/40">
                      <Check className="h-4 w-4 text-violet-600 mx-auto" strokeWidth={3} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400" data-testid="pricing-roadmap-note">
          Le module « Site de réservation en ligne » arrivera prochainement en option — roadmap 2027.
        </p>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center mb-10">
            <div className={`${OVERLINE} mb-2`}>Questions fréquentes</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              Tout savoir avant de vous lancer
            </h2>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {PRICING_FAQ.map((item) => (
              <FaqRow key={item.q} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="pricing-cta">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
          <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Prêt à vous lancer&nbsp;?
            </h2>
            <p className="mt-3 text-slate-400">Aucune installation. Soyez opérationnel dès maintenant.</p>
            <Link
              to="/register?context=vendor"
              className="btn-jn-light mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold"
              data-testid="pricing-cta-btn"
            >
              Profiter des 30 jours d'essai gratuit <ArrowRight className="btn-arrow h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
